import type { AlwaysAiUser, AppointmentListResponse } from "../../../shared/api-types";
import {
  appointmentsTodayCount,
  formatAppointmentTime,
  groupAppointments,
  shortDateLabel,
  upNextAppointment,
} from "./appointments";
import { can } from "./permissions";
import { activeAppointmentIdFor, todayCountLabel } from "./siderail-state";

// The single rail's Appointments entry (NO-754): a link to the Appointments
// list with its own chevron for Today. These are its decisions: whether the
// chevron shows, whether Today is open, which row carries the one highlight,
// and what Today says. The classic RD shell's entry (siderail-appointments.tsx)
// keeps its own rules. Pure and relative-import only, so `tsx --test` runs it.

// Today's open state is a per-user, per-browser preference, like the rail's
// density: the key carries the AlwaysAI user id and the value is only ever
// "0"/"1" (never PHI).
export function railTodayStorageKey(userId: string): string {
  return `alwaysai:rail-today-open:${userId}`;
}

/** Closed unless the viewer opened it: anything but "1" reads as closed. */
export function parseStoredRailTodayOpen(stored: string | null): boolean {
  return stored === "1";
}

export function serializeRailTodayOpen(open: boolean): string {
  return open ? "1" : "0";
}

/**
 * Is Today open? An open Appointment opens it by itself, without touching the
 * stored choice; a chevron click wins for the rest of this visit to the page
 * (the rail starts a fresh visit on every pathname change).
 */
export function railTodayOpen({
  stored,
  pathname,
  clickedThisVisit,
}: {
  stored: boolean;
  pathname: string;
  /** The open state a chevron click chose on this visit, or null. */
  clickedThisVisit: boolean | null;
}): boolean {
  if (clickedThisVisit !== null) return clickedThisVisit;

  return stored || activeAppointmentIdFor(pathname) !== null;
}

/**
 * An admin with no caseload and no RD in the URL: the server answers
 * `no_selection`, so there is no Today to show.
 */
export function railAppointmentsNoSelection(
  user: AlwaysAiUser,
  rdId: string | undefined
): boolean {
  const selectsOnly =
    can(user, "appointments:read:all") && !can(user, "appointments:read:own");

  return selectsOnly && rdId === undefined;
}

/**
 * Does the row show the Today chevron? Not on the Appointments list, which is
 * the whole schedule already, and not with no RD chosen.
 */
export function railAppointmentsChevron({
  onList,
  noSelection,
}: {
  onList: boolean;
  noSelection: boolean;
}): boolean {
  return !onList && !noSelection;
}

export function railTodayToggleLabel(open: boolean): string {
  return open ? "Hide today's Appointments" : "Show today's Appointments";
}

export type RailAppointmentsMark = "current" | "parent" | "none";

/**
 * How the Appointments row marks itself. On an open Appointment whose row
 * shows in Today, that row is the one highlight and the Appointments row only
 * names where you are; with the row out of sight (Today closed or folded away,
 * or the Appointment not among today's) the Appointments row keeps it.
 */
export function railAppointmentsMark({
  pathname,
  todayShowsActive,
}: {
  pathname: string;
  todayShowsActive: boolean;
}): RailAppointmentsMark {
  if (pathname === "/appointments") return "current";

  if (activeAppointmentIdFor(pathname) === null) return "none";

  return todayShowsActive ? "parent" : "current";
}

export const RAIL_TODAY_LINES = {
  loading: "Loading today…",
  unavailable: "Schedule unavailable",
  empty: "Nothing else today",
} as const;

export interface RailTodayRow {
  id: string;
  time: string;
  name: string;
  upNext: boolean;
  current: boolean;
}

export type RailTodayView =
  | { kind: "none" }
  | { kind: "line"; line: string }
  | { kind: "schedule"; heading: string; count: string; rows: RailTodayRow[] };

/** "3 left", "3+ left" past the first page, or nothing once the day is done. */
export function railTodayCountLabel(
  remaining: number,
  todayCount: number | null
): string {
  const count = todayCountLabel(remaining, todayCount);

  return count ? `${count} left` : "";
}

type AvailableList = Extract<AppointmentListResponse, { status: "available" }>;

function scheduleView(data: AvailableList, activeId: string | null): RailTodayView {
  const display = { now: data.now, timezone: data.timezone };
  const groups = groupAppointments(data.appointments, display);
  const todayCount = appointmentsTodayCount(groups, {
    ...display,
    hasNextPage: data.pageInfo.nextCursor !== null,
  });
  const remaining = groups.find((group) => group.label === "Today")?.appointments ?? [];
  const upNextId = upNextAppointment(groups, display)?.appointment.id ?? null;

  return {
    kind: "schedule",
    heading: `Today · ${shortDateLabel(display.now, display.timezone)}`,
    count: railTodayCountLabel(remaining.length, todayCount),
    rows: remaining.map((item) => ({
      id: item.id,
      time: formatAppointmentTime(item.startsAt, display.timezone),
      name: item.patientName || "Patient",
      upNext: item.id === upNextId,
      current: item.id === activeId,
    })),
  };
}

/**
 * What Today shows for the query's state. `no_selection` shows nothing: the
 * chevron is gone then, so this is only a guard.
 */
export function railTodayView({
  data,
  isError,
  activeAppointmentId,
}: {
  data: AppointmentListResponse | undefined;
  isError: boolean;
  activeAppointmentId: string | null;
}): RailTodayView {
  if (!data) {
    return {
      kind: "line",
      line: RAIL_TODAY_LINES[isError ? "unavailable" : "loading"],
    };
  }

  if (data.status === "no_selection") return { kind: "none" };

  if (data.status === "unavailable") {
    return { kind: "line", line: RAIL_TODAY_LINES.unavailable };
  }

  return scheduleView(data, activeAppointmentId);
}

/** Does Today list the open Appointment's row? */
export function railTodayShowsActive(view: RailTodayView): boolean {
  return view.kind === "schedule" && view.rows.some((row) => row.current);
}
